import { type ReactNode, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'

import { lanesFitBesideEachOther, useLayoutStep } from '../frame/layout'
import { Button } from './Control'
import { MarkupChoice } from './MarkupChoice'
import { Preview } from './preview/Preview'
import { PrintOut } from './preview/PrintOut'
import type { RendererFor } from './preview/renderers'
import { Waiting } from './Status'

/**
 * The editor is fetched when one is opened, and not before.
 *
 * CodeMirror is most of half a megabyte, and most visits to MyLib open a folder and read a PDF. Putting
 * it in the bundle everybody downloads would make looking at a document slower for the sake of writing
 * one — so it is a chunk of its own, and the first editor of a session waits a moment for it.
 */
const Editor = lazy(async () => {
  const module = await import('./Editor')
  return { default: module.Editor }
})

/**
 * **The** rich-text input of MyLib. There is exactly one, and there is never a second.
 *
 * Every place somebody writes prose uses this: a document, a note, a session's write-up, a task's
 * prompt, an answer, both sides of a flashcard, a sticky note on a board, what a subject is about, a
 * marking scheme, a note at an appointment. That is not tidiness — it is the only way a feature added
 * to writing is a feature everywhere. The rule is in `work/47-eine-rich-text-eingabe.md`, and it was
 * broken twice before it was written down.
 *
 * Two things follow and are not options:
 *
 * - **the markup is always switchable.** Which markup a text is in belongs to the text, and the
 *   control for it belongs *here* rather than beside each caller — put there once, every caller has
 *   it, and no new screen can forget it. [onFormat] is required for that reason.
 * - **`Editor` is not used directly by anybody.** It is the CodeMirror mount and an internal of this
 *   file. A view that reached for it would be a second rich-text input with fewer features, which is
 *   exactly how the two gaps came about. `webapp/src/ui/OneRichText.test.ts` asks the sources.
 *
 * Three more things it decides, each a rule from the issue rather than a matter of taste.
 *
 * **Text, preview, or both — and MyLib remembers which.** The choice belongs to the person and not to
 * the document, so it is kept in this browser and applies to whatever they open next. **On a narrow
 * screen there is no side by side**: two panes on a phone are two panes nobody can read, so the control
 * offers two states there and three on a wide one.
 *
 * **Side by side scrolls together, and that can be switched off.** Following along is what makes the
 * preview worth having while writing a long text; it is also exactly wrong when somebody is looking up
 * something at the top of the preview while typing at the bottom. Whichever pane is being scrolled leads,
 * and the other follows — decided here rather than by either of them, because both would otherwise chase
 * each other.
 */
export type Showing = 'text' | 'preview' | 'both'

/**
 * Where the choice is kept.
 *
 * One key per *purpose* rather than one for all of MyLib. A note is opened to write in and a
 * document that already says something is opened to read, so the two want different habits — and a
 * single key would mean the last thing touched decided for the other.
 */
const COUPLED = 'mylib.editor.coupled'

function keyFor(purpose: string): string {
  return `mylib.editor.showing.${purpose}`
}

function remembered<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const kept = window.localStorage.getItem(key)
    return allowed.includes(kept as T) ? (kept as T) : fallback
  } catch {
    // A browser with storage switched off, or a private window that refuses it. A remembered choice
    // is a convenience; losing it must never be a broken editor.
    return fallback
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // See above.
  }
}

export interface RichTextProps {
  readonly text: string
  /** Which markup this is, as a media type — the content's own. Decides what draws the preview. */
  readonly format: string
  readonly onChange: (text: string) => void
  /**
   * Writes the new markup down. **Required**, because the choice is always offered.
   *
   * Switching moves no byte of the text and adds nothing to a history — it is a label — so this is
   * asked nothing and offers both directions as often as anybody likes.
   */
  readonly onFormat: (format: string) => Promise<void>
  /**
   * Whether the chosen markup is remembered as what the *next* new thing is written in.
   *
   * The notepad does: a note is made by typing into it, so there is no moment at which anybody could
   * be asked. A document is made by a control that offers the languages itself and does not.
   */
  readonly rememberFormat?: boolean
  readonly readOnly?: boolean
  readonly placeholder?: string
  /** Names the two panes for a screen reader: "Notiz", "Skript Kapitel 3". */
  readonly label: string
  readonly autoFocus?: boolean
  /**
   * What to show when nothing has been chosen yet.
   *
   * A note is opened to write in, so it starts as text. A document that already says something is
   * opened to *read*, so it starts as the drawn text — the words, not the markup. Once somebody has
   * chosen, their choice wins: remembering it is what the issue asks for, and a default that kept
   * overruling it would be a control that does not stay put.
   */
  readonly initially?: Showing
  /**
   * Which habit this is. Kept apart per purpose, so the notepad and the library do not overwrite
   * each other's.
   */
  readonly purpose?: string
  /** Stands above the panes, beside the controls — where an area puts what it has to say. */
  readonly children?: ReactNode
  /** Handed straight to the preview. For a test, or for the day a renderer comes from somewhere else. */
  readonly rendererFor?: RendererFor
  /**
   * Whether the editor has the screen to itself, and how to ask for that.
   *
   * The control belongs *here*, beside Text and Preview, because it is the same kind of decision: how
   * much of the screen the writing gets. What "to itself" means is not the editor's business, though
   * — in the library it is a step in the address, so the frame's back walks out of it — which is why
   * the surface asks and the area answers. An area that leaves [onFull] out simply has no such
   * control, and nothing else changes.
   */
  readonly full?: boolean
  readonly onFull?: (full: boolean) => void
  readonly onSubmit?: () => void
  /**
   * Whether this text can be printed, and ⌘P here means "print it".
   *
   * Off by default, because not every text on a surface is a thing somebody prints: the two sides of a
   * flashcard are entered one after the other and a control for printing one of them would be noise.
   * A document is the case it exists for, and what gets printed is the *drawn* text, whole — see
   * `PrintOut`.
   */
  readonly printable?: boolean
  /**
   * Whether display controls take part in the form's ordinary Tab sequence.
   *
   * A pair of short, consecutive editors can opt out so Tab moves between the fields somebody is
   * filling in instead of stopping at display preferences between them. The controls remain visible
   * and clickable; ordinary reading and writing surfaces keep them in the sequence by default.
   */
  readonly controlsInTabOrder?: boolean
  /**
   * The small form, for somewhere there is no room for the full head.
   *
   * A sticky note on a board is 240 pixels wide and a note at an appointment is three lines high;
   * a row with Text/Preview/Both, "scroll together", print and full screen would be more control
   * than content. Compact keeps what a text cannot do without — writing, looking at it drawn, and
   * **the markup choice** — and drops the rest. It is a narrower head, never a second component.
   */
  readonly compact?: boolean
}

export function RichText({
  text,
  format,
  onChange,
  onFormat,
  rememberFormat = false,
  readOnly = false,
  placeholder,
  label,
  autoFocus = false,
  initially = 'both',
  purpose = 'text',
  children,
  rendererFor,
  full = false,
  onFull,
  onSubmit,
  printable = false,
  controlsInTabOrder = true,
  compact = false,
}: RichTextProps) {
  const step = useLayoutStep()
  // Side by side needs the room *and* a head wide enough to offer it. Compact has neither.
  const bothFit = lanesFitBesideEachOther(step) && !compact

  const [showing, setShowing] = useState<Showing>(() =>
    remembered<Showing>(keyFor(purpose), ['text', 'preview', 'both'], initially),
  )
  const [coupled, setCoupled] = useState<boolean>(
    () => remembered<'yes' | 'no'>(COUPLED, ['yes', 'no'], 'yes') === 'yes',
  )

  /*
   * Which pane is leading right now, and where it has got to.
   *
   * Only the follower is given a position; the leader is given `null`, which is what stops the two
   * pushing each other. The lead is dropped again shortly after the scrolling stops, so that the next
   * gesture in either pane is free to lead.
   */
  const [leader, setLeader] = useState<'text' | 'preview' | null>(null)
  const [at, setAt] = useState(0)
  const letGo = useRef<ReturnType<typeof setTimeout> | null>(null)

  const led = useCallback((by: 'text' | 'preview', fraction: number) => {
    setLeader(by)
    setAt(fraction)
    if (letGo.current !== null) {
      clearTimeout(letGo.current)
    }
    letGo.current = setTimeout(() => {
      setLeader(null)
    }, LEAD_LINGERS_MS)
  }, [])

  useEffect(
    () => () => {
      if (letGo.current !== null) {
        clearTimeout(letGo.current)
      }
    },
    [],
  )

  // Side by side needs both panes and the room for them. A narrow screen falls back to the text, which
  // is what somebody came to the editor for.
  const together = showing === 'both' && bothFit
  const showText = showing === 'text' || together || (showing === 'both' && !bothFit)
  const showPreview = showing === 'preview' || together

  /*
   * Printing, while the dialog is being made ready and stands open.
   *
   * ⌘P is taken over rather than left to the browser, and that is the one keyboard shortcut in MyLib
   * that replaces something the browser already does. The reason is that the browser's own print is
   * the broken one here: it photographs a screen made of panes that scroll, and what it can print of a
   * long text is the part somebody happens to be looking at. Taking the key means the same thing
   * happens whether it was pressed or the control beside it was used — and the control is there, so
   * nothing depends on knowing the shortcut.
   */
  const [printing, setPrinting] = useState(false)
  const printed = useCallback(() => {
    setPrinting(false)
  }, [])

  useEffect(() => {
    if (!printable || compact) {
      return
    }
    const onKey = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'p'
      ) {
        event.preventDefault()
        setPrinting(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [printable, compact])

  const choose = (wanted: Showing) => {
    setShowing(wanted)
    remember(keyFor(purpose), wanted)
  }

  const follow = together && coupled

  return (
    <div className={together ? 'surface surface--split' : 'surface'}>
      <div className={compact ? 'surface__head surface__head--compact' : 'surface__head'}>
        {/*
          The markup, always. It stands first because it says what the text *is*, where everything
          beside it only says how it is being looked at — and because a control that is always in the
          same place is a control nobody has to find twice.
        */}
        <MarkupChoice
          format={format}
          onChoose={onFormat}
          remember={rememberFormat}
          readOnly={readOnly}
          tabIndex={controlsInTabOrder ? undefined : -1}
        />
        <div className="surface__modes" role="group" aria-label="What to show">
          <Button
            tabIndex={controlsInTabOrder ? undefined : -1}
            tone={showing === 'text' ? 'quiet' : 'bare'}
            aria-pressed={showing === 'text'}
            onClick={() => {
              choose('text')
            }}
          >
            Text
          </Button>
          <Button
            tabIndex={controlsInTabOrder ? undefined : -1}
            tone={showing === 'preview' ? 'quiet' : 'bare'}
            aria-pressed={showing === 'preview'}
            onClick={() => {
              choose('preview')
            }}
          >
            Preview
          </Button>
          {/*
            Only where there is room for it. Offering "side by side" on a phone would be offering
            something that cannot be honoured, and a control that lies is worse than one that is absent.
          */}
          {bothFit && (
            <Button
              tabIndex={controlsInTabOrder ? undefined : -1}
              tone={showing === 'both' ? 'quiet' : 'bare'}
              aria-pressed={showing === 'both'}
              onClick={() => {
                choose('both')
              }}
            >
              Both
            </Button>
          )}
        </div>

        {printable && !compact && (
          /*
            Beside "how much of the screen", because it is the same kind of question: where this text
            is being read. On paper it is read whole, so this is never a mode the surface goes into —
            it draws the text once more, prints it, and is gone again.
          */
          <Button
            tabIndex={controlsInTabOrder ? undefined : -1}
            tone="bare"
            icon="print"
            iconOnlyLabel="Print"
            disabled={printing}
            onClick={() => {
              setPrinting(true)
            }}
          />
        )}

        {onFull !== undefined && !compact && (
          <Button
            tabIndex={controlsInTabOrder ? undefined : -1}
            tone="bare"
            icon={full ? 'collapse' : 'expand'}
            iconOnlyLabel={full ? 'Leave full screen' : 'Full screen'}
            aria-pressed={full}
            onClick={() => {
              onFull(!full)
            }}
          />
        )}

        {together && (
          /*
            A chip with `aria-pressed`, like the three beside it — not a checkbox.

            It was one, and it was listed as a known deviation for long enough to be worth saying why
            it is not any more: it sits in the one input every area writes in, so it is the deviation
            that gets copied fastest. On/off is a state of a control, and the control next to it
            already says so that way.
          */
          <Button
            tabIndex={controlsInTabOrder ? undefined : -1}
            tone={coupled ? 'quiet' : 'bare'}
            aria-pressed={coupled}
            onClick={() => {
              const wanted = !coupled
              setCoupled(wanted)
              remember(COUPLED, wanted ? 'yes' : 'no')
            }}
          >
            Scroll together
          </Button>
        )}

        {children}
      </div>

      <div className="surface__panes">
        {showText && (
          <div className="surface__pane surface__pane--text">
            <Suspense fallback={<Waiting label="Opening the editor." />}>
              <Editor
                text={text}
                format={format}
                onChange={onChange}
                readOnly={readOnly}
                placeholder={placeholder}
                label={`${label} — text`}
                autoFocus={autoFocus}
                onScroll={
                  follow
                    ? (fraction) => {
                        led('text', fraction)
                      }
                    : undefined
                }
                scrollTo={follow && leader === 'preview' ? at : null}
                onSubmit={onSubmit}
              />
            </Suspense>
          </div>
        )}
        {showPreview && (
          <div className="surface__pane surface__pane--preview">
            <Preview
              text={text}
              format={format}
              label={`${label} — preview`}
              rendererFor={rendererFor}
              onScroll={
                follow
                  ? (fraction) => {
                      led('preview', fraction)
                    }
                  : undefined
              }
              scrollTo={follow && leader === 'text' ? at : null}
            />
          </div>
        )}
      </div>

      {printing && (
        <PrintOut
          text={text}
          format={format}
          title={label}
          rendererFor={rendererFor}
          onDone={printed}
        />
      )}
    </div>
  )
}

/**
 * How long the pane that was scrolled keeps the lead.
 *
 * Long enough to cover the gap between two flicks of a finger, short enough that putting the cursor in
 * the other pane and scrolling it works immediately.
 */
const LEAD_LINGERS_MS = 400
